import { Button, Modal, Space, notification } from 'antd';
import React, { FC } from 'react';
import { ReactComponent as TurnbackIcon } from 'assets/icons/turnback.svg';
import { IModal } from '../../../../common/models/model.interface';
import {
  ICheckResult,
  IMatchCandidate,
  IVisitorBanListEntry,
} from 'common/services/visitor-ban-list-service/visitor-ban-list.service.types.';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';
import './advisory-alert-modal.scss';
import { ILicensePlateDrivingPrivilegesRead } from 'common/services/citation-service/citation.service.types';
import { formatCustomDateTime } from 'common/helpers/date.helper';

export type AdvisoryAlertModalType = 'BanList' | 'DrivingPrivileges';

export interface IAdvisoryAlertModalProps {
  data: ICheckResult | ILicensePlateDrivingPrivilegesRead;
  type: AdvisoryAlertModalType;
  isVacantAddress: boolean;
  isShowReason: boolean;
}

const AdvisoryAlertModal: FC<IModal<IAdvisoryAlertModalProps, any>> = ({ isOpen, onClose, title, initData }) => {
  const hideModal = (...rest: any) => {
    notification.destroy();
    if (onClose) {
      onClose(...rest);
    }
  };

  const handleTurnbackAdmit = () => {
    hideModal(true, 'turnback');
  };

  const handleClickAdmit = () => {
    hideModal(true, 'admit');
  };

  const getItem = (visitorBanEntry: IVisitorBanListEntry) => {
    const { type, firstName, lastName, companyName, licensePlate, action, reason } = visitorBanEntry;

    if (type !== 'person' && type !== 'company' && type !== 'vehicle') {
      return <></>;
    }

    const content = (
      <>
        {type === 'person' && (
          <div className="item">
            <span className="title">Name:</span> {firstName} {lastName}
          </div>
        )}
        {(type === 'company' || (type === 'person' && companyName)) && (
          <div className="item">
            <span className="title">Company:</span> {companyName}
          </div>
        )}
        {(type === 'vehicle' || (type === 'person' && licensePlate)) && (
          <div className="item">
            <span className="title">License plate:</span> {licensePlate}
          </div>
        )}
        {action && (
          <div className="item">
            <span className="title">Action:</span> {action}
          </div>
        )}
        {initData?.isShowReason && reason && (
          <div className="item">
            <span className="title">Reason:</span> {reason}
          </div>
        )}
      </>
    );

    return content;
  };

  const getMatchCandidateAlertBlock = (index: number, candidate: IMatchCandidate) => {
    const { decision, visitorBanEntry } = candidate;

    return (
      <div className="match-candidate-wrapper" key={`advisory-match-candidate-${decision}-wrapper-${index}`}>
        <div className={`point ${decision}`}></div>
        <div>
          <div>
            <span className="title">{decision === 'match' ? 'Exact Match' : 'Partial Match'}</span>
          </div>

          {getItem(visitorBanEntry)}
        </div>
      </div>
    );
  };

  const getBanListAlert = (data: ICheckResult) => {
    return (
      <>
        {data?.hasMatch && (
          <div className="block-wrapper">
            {data.candidates
              .filter((candidate) => candidate.decision === 'match')
              .map((candidate, index) => {
                return getMatchCandidateAlertBlock(index, candidate);
              })}
          </div>
        )}

        {data?.hasPartialMatch && (
          <div className="block-wrapper">
            {data.candidates
              .filter((candidate) => candidate.decision === 'partialMatch')
              .map((candidate, index) => {
                return getMatchCandidateAlertBlock(index, candidate);
              })}
          </div>
        )}
      </>
    );
  };

  const getDrvingPrivilageAlert = (data: ILicensePlateDrivingPrivilegesRead) => {
    return (
      <div className="block-wrapper">
        <div className="match-candidate-wrapper">
          <div>
            <div>
              <span className="title">Repeated Visitor Speeding Violation</span>
            </div>
            <div>
              <span className="title">Action: Driving Privileges Revoked</span>
            </div>

            <div style={{ marginTop: '18px' }}>
              <span className="title">License Plate:</span> {data.licensePlate}
            </div>
            <div>
              <span className="title">Revoke Duration:</span> {data.days}
            </div>
            <div style={{ marginBottom: '18px' }}>
              <span className="title">Ends:</span>
              {formatCustomDateTime(data.revokUntil)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getBody = () => {
    if (initData?.type === 'BanList') {
      return getBanListAlert(initData.data as ICheckResult);
    }
    if (initData?.type === 'DrivingPrivileges') {
      return getDrvingPrivilageAlert(initData.data as ILicensePlateDrivingPrivilegesRead);
    }
    return <></>;
  };

  return (
    <Modal
      centered
      title={<span className="advisory-alert-title">Advisory Alert</span>}
      width={600}
      open={isOpen}
      wrapClassName="custom-modal custom-modal--so"
      closable={true}
      onCancel={() => hideModal(false)}>
      <Space className="full-width mb-12 visitor-ban-alert-wrapper" size={8} direction="vertical">
        {getBody()}
      </Space>
      <Space className="footer" style={{ paddingTop: 0 }}>
        <div className="advisory-footer-content">
          <span>If approved anyway, the entry will be flagged</span>
          <div className="buttons">
            {!initData?.isVacantAddress && (
              <Button className="important-btn eiq-button" icon={<TurnbackIcon />} onClick={handleTurnbackAdmit}>
                Turn Back
              </Button>
            )}
            <Button type="primary" className="primary-btn mobile-btn" icon={<AdmitIcon />} onClick={handleClickAdmit}>
              Approve Anyway
            </Button>
          </div>
        </div>
      </Space>
    </Modal>
  );
};

export default React.memo(AdvisoryAlertModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});
